"use client";

import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

import { LENGTH_OPTIONS } from "../../lib/length";
import type { AiPromptLength } from "../../lib/types";

interface LengthRadioProps {
  value: AiPromptLength;
  onChange: (next: AiPromptLength) => void;
  disabled?: boolean;
}

const MIN_LENGTH = 1;
const MAX_LENGTH = 5;

/**
 * Ideal-length slider — Figma `9521:852` ("Slider").
 *
 * Renders the 1..5 sentence-count picker as a Radix slider with a track of
 * five tick labels underneath. The active tick is bolded (text-foreground)
 * to match Figma; inactive ticks fade to `text-contrast/50`.
 *
 * Component is named `LengthRadio` for backwards compatibility with
 * existing imports — the underlying control is now a slider rather than a
 * RadioGroup.
 */
export function LengthRadio({ value, onChange, disabled }: LengthRadioProps) {
  return (
    <div className="flex flex-col gap-2">
      <Slider
        variant="light"
        value={[value]}
        min={MIN_LENGTH}
        max={MAX_LENGTH}
        step={1}
        onValueChange={(values) => {
          const next = values[0];
          if (typeof next === "number" && next >= MIN_LENGTH && next <= MAX_LENGTH) {
            onChange(next as AiPromptLength);
          }
        }}
        disabled={disabled}
        aria-label="Ideal length"
      />
      <div className="flex items-center justify-between text-xs leading-4">
        {LENGTH_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            aria-label={`Ideal length ${option.label}`}
            aria-pressed={option.value === value}
            className={cn(
              "cursor-pointer text-center transition-colors",
              option.value === value
                ? "font-medium text-foreground"
                : "text-contrast/50 hover:text-contrast/70",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
